import { QueryClient } from "@tanstack/react-query";

import { DATA_KEYS } from "@/utils/useRemoteData";

/**
 * Bring the builder back in step with an agent whose capability versions just
 * changed.
 *
 * The order matters. The builder resets its form from the assistant prop when
 * the remount atom changes, so bumping that before the refetch has landed
 * resets the form to the pins it already had: the update looks like it did
 * nothing, and the next autosave writes the pre-update shape back over the
 * migration.
 *
 * Shared by both capability dialogs so the sequence has one definition rather
 * than two that can drift.
 */
export const refreshAfterCapabilityUpdate = async (
  queryClient: QueryClient,
  assistantId: string,
  remountAgentForm: () => void
) => {
  queryClient.invalidateQueries({
    queryKey: [DATA_KEYS.ASSISTANT_CAPABILITIES, assistantId],
  });
  queryClient.invalidateQueries({
    queryKey: [DATA_KEYS.ASSISTANT_CAPABILITY_UPDATES, assistantId],
  });
  // The definition before the update is saved as a new agent version.
  queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANT_VERSIONS] });
  queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANTS] });

  await queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANT_ID] });
  // A settings version can change the form itself — a field appears,
  // disappears, or swaps component — so the schema has to be refetched, not
  // just the agent. Prefix key: every cached version is now suspect.
  await queryClient.invalidateQueries({ queryKey: [DATA_KEYS.AGENT_FORM] });

  remountAgentForm();
};
